import { AlertCircle, RefreshCw } from 'lucide-react'

import { Button } from '@/components/common/Button/Button'
import { cn } from '@/utils/cn'

import type { ErrorStateProps } from '../DataTable.types'

export const TableError: React.FC<ErrorStateProps> = ({ error, onRetry }) => {
  const getErrorMessage = (error: Error): string => {
    // Handle common error types
    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      return 'Error de conexión. Verifica tu conexión a internet.'
    }

    if (error.message.includes('404')) {
      return 'El recurso solicitado no fue encontrado.'
    }

    if (error.message.includes('403')) {
      return 'No tienes permisos para acceder a esta información.'
    }

    if (error.message.includes('500')) {
      return 'Error interno del servidor. Intenta nuevamente en unos minutos.'
    }

    // Return original error message or a generic fallback
    return error.message || 'Ocurrió un error inesperado al cargar los datos.'
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
        <AlertCircle className="h-6 w-6 text-red-600" />
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-2">Error al cargar datos</h3>

      <p className="text-muted-foreground text-center mb-6 max-w-md leading-relaxed">
        {getErrorMessage(error)}
      </p>

      {onRetry && (
        <Button variant="secondary" onClick={onRetry} leftIcon={<RefreshCw className="h-4 w-4" />}>
          Intentar nuevamente
        </Button>
      )}

      {/* Development error details */}
      {process.env.NODE_ENV === 'development' && (
        <details className="mt-6 w-full max-w-md">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
            Detalles del error (desarrollo)
          </summary>
          <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto max-h-32">
            <code>{error.stack || error.message}</code>
          </pre>
        </details>
      )}
    </div>
  )
}

// Inline error for specific cells or components
export const InlineError: React.FC<{
  message: string
  onRetry?: () => void
  className?: string
}> = ({ message, onRetry, className }) => (
  <div className={cn('flex items-center gap-2 text-red-600', className)}>
    <AlertCircle className="h-4 w-4 flex-shrink-0" />
    <span className="text-sm">{message}</span>
    {onRetry && (
      <Button variant="ghost" size="sm" onClick={onRetry} className="h-6 px-2 text-xs">
        Reintentar
      </Button>
    )}
  </div>
)

// Error boundary fallback for table components
export const TableErrorBoundary: React.FC<{
  error: Error
  resetError: () => void
}> = ({ error, resetError }) => (
  <div className="border border-red-200 rounded-md p-6 bg-red-50">
    <div className="flex items-start gap-3">
      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h3 className="font-medium text-red-800 mb-1">Error en el componente de tabla</h3>
        <p className="text-sm text-red-700 mb-3">
          Ocurrió un error inesperado. Intenta recargar la página.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={resetError}
          className="border-red-300 text-red-700 hover:bg-red-100"
        >
          Reintentar
        </Button>
      </div>
    </div>

    {process.env.NODE_ENV === 'development' && (
      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-medium text-red-800">
          Detalles técnicos
        </summary>
        <pre className="mt-2 p-3 bg-red-100 rounded text-xs overflow-auto max-h-32">
          <code>{error.stack}</code>
        </pre>
      </details>
    )}
  </div>
)
